import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  TextField,
} from "@mui/material";
import { dummyGSTData } from "../../../../../dummyData";

const GSTReportTable = ({
  searchQuery,
  visibleColumns,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  showTotals = false,
}) => {
  const [data, setData] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [totals, setTotals] = useState({
    taxableAmount: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    totalAmount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  // Get unique firms for filter
  const firms = [
    ...new Set(dummyGSTData.invoiceData.map(item => item.firmName)),
  ];

  // Add "All Firms" option at the beginning of the options array
  const firmOptions = [
    { label: "All Firms", value: "" },
    ...firms.map(firm => ({ label: firm, value: firm })),
  ];

  useEffect(() => {
    setIsLoading(true);

    // Simulate API delay
    setTimeout(() => {
      // Filter data based on search query and selected firm
      let filtered = [...dummyGSTData.invoiceData];

      if (selectedFirm && selectedFirm.value !== "") {
        filtered = filtered.filter(
          item => item.firmName === selectedFirm.value,
        );
      }

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          item =>
            item.invoiceNo.toLowerCase().includes(query) ||
            item.firmName.toLowerCase().includes(query) ||
            item.gstin.toLowerCase().includes(query),
        );
      }

      setFilteredData(filtered);

      // Calculate totals
      const newTotals = filtered.reduce(
        (acc, item) => {
          return {
            taxableAmount: acc.taxableAmount + item.taxableAmount,
            cgst: acc.cgst + item.cgst,
            sgst: acc.sgst + item.sgst,
            igst: acc.igst + item.igst,
            totalAmount: acc.totalAmount + item.totalAmount,
          };
        },
        {
          taxableAmount: 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          totalAmount: 0,
        },
      );

      setTotals(newTotals);

      // Paginate the data
      const startIndex = page * rowsPerPage;
      const paginatedData = filtered.slice(
        startIndex,
        startIndex + rowsPerPage,
      );
      setData(paginatedData);

      setIsLoading(false);
    }, 500);
  }, [searchQuery, selectedFirm, page, rowsPerPage]);

  const handleFirmChange = (event, newValue) => {
    setSelectedFirm(newValue);
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: "none",
        border: "1px solid #E5E7EB",
        borderRadius: "0.5rem",
      }}>
      {/* Filter Section */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Autocomplete
            value={selectedFirm}
            onChange={handleFirmChange}
            options={firmOptions}
            getOptionLabel={option => option.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            sx={{
              width: 250,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#E5E7EB",
                },
                "&:hover fieldset": {
                  borderColor: "#5652B0",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#5652B0",
                },
              },
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Filter by Firm"
                size="small"
                sx={{
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#5652B0",
                  },
                }}
              />
            )}
          />
        </div>
        <div className="text-sm text-gray-600">
          Displaying {filteredData.length} entries
        </div>
      </div>

      {/* GST Report Table */}
      <TableContainer
        sx={{ maxHeight: "calc(100vh - 370px)" }}
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumns.invoiceNo && (
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  Invoice No
                </TableCell>
              )}
              {visibleColumns.date && (
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  Date
                </TableCell>
              )}
              {visibleColumns.firmName && (
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  Firm Name
                </TableCell>
              )}
              {visibleColumns.gstin && (
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  GSTIN
                </TableCell>
              )}
              {visibleColumns.taxableAmount && (
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  Taxable Amount
                </TableCell>
              )}
              {visibleColumns.cgst && (
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  CGST
                </TableCell>
              )}
              {visibleColumns.sgst && (
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  SGST
                </TableCell>
              )}
              {visibleColumns.igst && (
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  IGST
                </TableCell>
              )}
              {visibleColumns.totalAmount && (
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  Total Amount
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={Object.values(visibleColumns).filter(v => v).length}
                  align="center">
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size={40} />
                  </div>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={Object.values(visibleColumns).filter(v => v).length}
                  align="center">
                  <div className="py-4 text-gray-500">No data found</div>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {data.map(row => (
                  <TableRow
                    key={row.invoiceNo}
                    hover
                    sx={{
                      "&:hover": { backgroundColor: "#F9FAFB" },
                    }}>
                    {visibleColumns.invoiceNo && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {row.invoiceNo}
                      </TableCell>
                    )}
                    {visibleColumns.date && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {row.date}
                      </TableCell>
                    )}
                    {visibleColumns.firmName && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {row.firmName}
                      </TableCell>
                    )}
                    {visibleColumns.gstin && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {row.gstin}
                      </TableCell>
                    )}
                    {visibleColumns.taxableAmount && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        ₹{row.taxableAmount.toLocaleString()}
                      </TableCell>
                    )}
                    {visibleColumns.cgst && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        ₹{row.cgst.toLocaleString()}
                      </TableCell>
                    )}
                    {visibleColumns.sgst && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        ₹{row.sgst.toLocaleString()}
                      </TableCell>
                    )}
                    {visibleColumns.igst && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        ₹{row.igst.toLocaleString()}
                      </TableCell>
                    )}
                    {visibleColumns.totalAmount && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        ₹{row.totalAmount.toLocaleString()}
                      </TableCell>
                    )}
                  </TableRow>
                ))}

                {/* Totals Row */}
                {showTotals && (
                  <TableRow
                    sx={{
                      backgroundColor: "#F9FAFB",
                      borderTop: "2px solid #E5E7EB",
                    }}>
                    {visibleColumns.invoiceNo && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                        className="font-outfit">
                        Total
                      </TableCell>
                    )}
                    {visibleColumns.date && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                        }}
                        className="font-outfit">
                        -
                      </TableCell>
                    )}
                    {visibleColumns.firmName && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                        }}
                        className="font-outfit">
                        -
                      </TableCell>
                    )}
                    {visibleColumns.gstin && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                        }}
                        className="font-outfit">
                        -
                      </TableCell>
                    )}
                    {visibleColumns.taxableAmount && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                        className="font-outfit">
                        ₹{totals.taxableAmount.toLocaleString()}
                      </TableCell>
                    )}
                    {visibleColumns.cgst && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                        className="font-outfit">
                        ₹{totals.cgst.toLocaleString()}
                      </TableCell>
                    )}
                    {visibleColumns.sgst && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                        className="font-outfit">
                        ₹{totals.sgst.toLocaleString()}
                      </TableCell>
                    )}
                    {visibleColumns.igst && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                        className="font-outfit">
                        ₹{totals.igst.toLocaleString()}
                      </TableCell>
                    )}
                    {visibleColumns.totalAmount && (
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#374151",
                        }}
                        className="font-outfit">
                        ₹{totals.totalAmount.toLocaleString()}
                      </TableCell>
                    )}
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{
          borderTop: "1px solid #E5E7EB",
          ".MuiTablePagination-select": {
            borderRadius: "0.375rem",
          },
        }}
      />

      {isLoading && (
        <div className="absolute top-0 left-0 right-0">
          <div className="h-1 w-full overflow-hidden">
            <div className="animate-progress bg-[#5652B0] h-1"></div>
          </div>
        </div>
      )}
    </Paper>
  );
};

export default GSTReportTable;
